import { ApiClient } from '../../../api-client';

import {
  formatBidCategory,
  formatBidDetailsQuantity,
  formatBidDetailsResult,
  formatBiddingInfoResponse,
  formatBidEfficiencyData,
  formatBidHistoryResponse,
  formatBidItemConditionResponse,
  formatBidRankResponse,
  formatBidSearchResults,
  formatRankReportResponse,
  formatTimedAuctionInfoResponse,
  formatStagedBids,
  formatStagedBidsReport,
  formatUploadedBids,
} from './transformers';
import {
  IBidHistoryQueryOptions,
  IBidHistoryResponse,
  IBidItemCondition,
  IBidStatusUpdateResponseData,
  IBidValidityConfigurationsResponse,
  IBiddingWindowsResponse,
  ICreateOrUpdateBidPayload,
  IExportBidsQueryOptions,
  IFetchAllBidsPaasResponse,
  IFetchAllBidsPayload,
  IFetchAllBidsRawData,
  IFetchAllBidsRawResponse,
  IFetchAllBidsResponse,
  IFetchAllStagedBidsReportPayload,
  IFetchAllStagedBidsReportResponse,
  IFetchAllUploadedBidsPayload,
  IFetchAllUploadedBidsResponse,
  IFetchBidDetailsResponse,
  IFetchBiddingWindowInfoResponse,
  IFetchBidItemConditionRawResponse,
  IFetchTimedAuctionTimePeriodResponse,
  IFetchBidsQuantityResponse,
  IFetchBidsRankResponse,
  IFetchBidStepperValResponse,
  IFetchRankReportPayload,
  IFetchStagedBidsPayload,
  IFetchStagedBidsResponse,
  IFetchUploadBidFilesResponse,
  IRankReportsRaw,
  IRanksDetailedReportAdminFilterQueryParams,
  IRanksDetailedReportAdminResponseRaw,
  IStoreDetailsResponseRaw,
  IUploadBidBookPayload,
  IUploadBidBookTemplatePayload,
  IUploadFileOptions,
  IUploadFileResponse,
  ISearchBidsOptions,
  TBidCategoryFiltersResponse,
  TBidModelsFiltersResponse,
  TBidModelsFiltersUrlOptions,
  TFetchUploadBidFilesPayload,
  IFetchDownloadBidsTemplatePayload,
  IBidValidityConfigurationQueryParams,
} from './types';
import {
  // downloadBidBookUrl,
  bidCategoryFiltersUrl,
  bidModelsFiltersUrl,
  bidRankUrl,
  bidSearchPaasUrl,
  bidSearchUrl,
  bidStatusUpdateUrl,
  downloadBidFileTemplateUrl,
  downloadDealerBidTemplateUrl,
  downloadUploadBidFileTemplateUrl,
  fetchBidDetailsUrl,
  fetchBiddingWindowInfoUrl,
  fetchBidHistoryUrl,
  fetchTimedAuctionTimePeriodUrl,
  fetchBidItemConditionUrl,
  fetchBidsQuantityUrl,
  fetchBidValidityConfigurationUrl,
  fetchBiddingWindowsByValidityConfigUrl,
  fetchExportBidsExcelUrl,
  fetchRanksDetailedReportAdminUrl,
  fetchStagedBidsUrl,
  fetchStepperValueUrl,
  fetchStoreDetailsUrl,
  fetchUploadBidFilesUrl,
  rankReportUrl,
  stagedBidsReportSearchUrl,
  updateBidUrl,
  uploadBidBookUrl,
  uploadBidFileUrl,
  uploadBidTemplateFileUrl,
  uploadedBidSearchUrl,
} from './urls';

/**
 * This is provides the API client methods for all bids page,
 * filters dropdowns and components admin services like category, brands and
 * rank report etc.
 */
export class BidsAPIClient extends ApiClient {
  private static classInstance?: BidsAPIClient;

  private constructor() {
    super({
      apiBaseURL: 'ADMIN_SERVICE_BASE_URL',
    });
  }

  /**
   * Applying the dreaded singleton pattern here to reuse the axios instance.
   */
  public static getClientInstance = () => {
    if (!this.classInstance) {
      this.classInstance = new BidsAPIClient();
    }

    return this.classInstance;
  };

  /**
   * Api call to fetch the rank filters, category and brand filters options data.
   *
   * @returns TBidCategoryFiltersResponse
   */
  public fetchAllBidCategoryFilters = async () => {
    const response = await this.get<TBidCategoryFiltersResponse>(
      bidCategoryFiltersUrl()
    );

    if (!response.success) throw response;

    return formatBidCategory(response?.data?.data);
  };

  /**
   * Api call to fetch the rank filters, category and brand filters options data.
   */
  public fetchAllBidModelsFilters = async (
    queryParams: TBidModelsFiltersUrlOptions
  ) => {
    const response = await this.get<TBidModelsFiltersResponse>(
      bidModelsFiltersUrl(queryParams)
    );

    if (!response.success) throw response;

    return response.data.data;
  };

  /**
   * Api call to fetch the bids by category and brands.
   */
  public searchAllBids = async (params: ISearchBidsOptions) => {
    const { isRankOneBidDisplayEnabled, payload, urlParams } = params;
    const response = await this.post<
      IFetchAllBidsRawResponse | IFetchAllBidsResponse
    >(bidSearchUrl(urlParams), payload);

    if (!response.success) throw response;

    return formatBidSearchResults(
      (response?.data?.data as IFetchAllBidsRawData) || {},
      payload.page,
      isRankOneBidDisplayEnabled
    );
  };

  /**
   * Api call to fetch the bids by category and brands for PAAS.
   *
   * @returns IFetchBidsByCategoryResponse
   * @deprecated - remove this when ready.
   */
  public searchAllBidsPaas = async (queryParams: IFetchAllBidsPayload) => {
    const response = await this.post<IFetchAllBidsPaasResponse>(
      bidSearchPaasUrl(),
      queryParams
    );

    if (!response.success) throw response;

    return response?.data.data;
  };

  // TODO: Remove this once `useUploadBidBookTemplateMutation` hook is stable.
  // /**
  //  * Api call to download the paas bids by filters.
  //  *
  //  * @returns IFetchBidsByCategoryResponse
  //  */
  // public downloadBidBookPaas = async (queryParams: IFetchAllBidsPayload) => {
  //   const response = await this.post<BlobPart>(
  //     bidSearchPaasUrl(),
  //     queryParams,
  //     {
  //       responseType: 'blob',
  //     }
  //   );

  //   if (!response.success) throw response;

  //   return response;
  // };

  /**
   * Api call to fetch the quantity dropdown.
   */
  public fetchBidsQuantity = async (assetId: string) => {
    const response = await this.get<IFetchBidsQuantityResponse>(
      fetchBidsQuantityUrl(assetId)
    );

    if (!response.success) throw response;

    return formatBidDetailsQuantity(response.data.data || []);
  };

  /**
   * Api call to create bid for current user.
   */
  public createBidStatusRequest = async (
    payload: ICreateOrUpdateBidPayload
  ) => {
    const { assetId, ...restPayload } = payload;
    let localPayload = { ...restPayload };

    if (!payload.bidId) {
      const { bidId, ...rest } = localPayload;
      localPayload = rest;
    }

    if (!payload.spotPrice) {
      const { spotPrice, ...rest } = localPayload;
      localPayload = rest;
    }

    if (!payload.maxQuantity) {
      const { maxQuantity, ...rest } = localPayload;
      localPayload = rest;
    }

    if (!payload.autoTopupEnabled) {
      const { autoTopupEnabled, ...rest } = localPayload;
      localPayload = rest;
    }

    const response = await this.post<IBidStatusUpdateResponseData>(
      bidStatusUpdateUrl(assetId),
      localPayload
    );

    if (!response.success) throw response;

    return response;
  };

  /**
   * Api call to upload Bid book.
   */
  public uploadBidBook = async (payload: IUploadBidBookPayload) => {
    const response = await this.post<IUploadFileResponse>(
      uploadBidBookUrl(),
      payload.file,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        timeout: 1 * 60 * 1000, // Force a 1 minute wait on uploads.
      }
    );

    if (!response.success) throw response;

    return response;
  };

  // TODO: Remove this once the `useDownloadBidTemplateQuery` is stable.
  // /**
  //  * This provides download authentication services. Mainly expects a categoryId
  //  * and BrandId.
  //  */
  // public downloadBidBookRequest = async (
  //   queryParams: IDownloadBidBookUrlOptions
  // ) => {
  //   const response = await this.get<BlobPart>(downloadBidBookUrl(queryParams), {
  //     responseType: 'blob',
  //   });

  //   if (!response.success) throw response;

  //   return response.data;
  // };

  /**
   * Api call to fetch the bid History.
   */
  public fetchBidHistory = async (
    queryParams: IBidHistoryQueryOptions,
    isRankOneBidDisplayEnabled: boolean = false
  ) => {
    const response = await this.get<IBidHistoryResponse>(
      fetchBidHistoryUrl(queryParams)
    );

    if (!response.success) throw response;

    return formatBidHistoryResponse(
      response?.data?.data,
      isRankOneBidDisplayEnabled
    );
  };

  /**
   * Api call to fetch rank report
   */
  public fetchRankReports = async (payload?: IFetchRankReportPayload) => {
    const response = await this.post<IRankReportsRaw>(rankReportUrl(), payload);

    if (!response.success) throw response;

    return formatRankReportResponse(response.data);
  };

  /**
   * Api call to fetch stepper value for particular bid using bid id.
   */
  public fetchBidStepperValue = async (assetId: string) => {
    const response = await this.get<IFetchBidStepperValResponse>(
      fetchStepperValueUrl(assetId)
    );

    if (!response.success) throw response;

    return response?.data?.data?.minIncrementVal;
  };

  /**
   * Api call to create or update bid for current user.
   */
  public updateBidRequest = async (payload: ICreateOrUpdateBidPayload) => {
    const { _id, maxQuantity, ...rest } = payload;

    const localPayload = {
      ...rest,
      ...(_id ? { _id } : {}),
      ...(maxQuantity ? { maxQuantity } : {}),
    };

    let response;

    if (payload._id) {
      response = await this.put<IBidStatusUpdateResponseData>(
        updateBidUrl(),
        localPayload
      );
    } else {
      response = await this.post<IBidStatusUpdateResponseData>(
        updateBidUrl(),
        localPayload
      );
    }

    if (!response.success) throw response;

    return response;
  };

  /**
   * Api call to fetch Item condition for particular bid using bid id.
   * IFetchBidItemConditionRawResponse,
   * IBidItemCondition,
   */
  public fetchBidItemCondition = async (assetId: string) => {
    const response = await this.get<
      IFetchBidItemConditionRawResponse | IBidItemCondition[]
    >(fetchBidItemConditionUrl(assetId));

    if (!response.success) throw response;

    return formatBidItemConditionResponse(
      response.data as IFetchBidItemConditionRawResponse
    );
  };

  /**
   * Api call to fetch all the bids rank.
   */
  public fetchBidsRanks = async (payload?: IFetchRankReportPayload) => {
    const response = await this.post<IFetchBidsRankResponse>(
      bidRankUrl(),
      payload
    );

    if (!response.success) throw response;

    return formatBidRankResponse(response?.data?.data);
  };

  /**
   * Api call to fetch bid details
   */
  public fetchBidDetails = async (
    assetId: string,
    isRankOneBidDisplayEnabled: boolean
  ) => {
    const response = await this.get<IFetchBidDetailsResponse>(
      fetchBidDetailsUrl(assetId)
    );

    if (!response.success) throw response;

    return formatBidDetailsResult(
      response.data?.data,
      isRankOneBidDisplayEnabled
    );
  };

  /**
   * Api call to fetch the upload bids files by category.
   */
  public fetchUploadBidFiles = async (
    queryParams: TFetchUploadBidFilesPayload
  ) => {
    const response = await this.get<IFetchUploadBidFilesResponse>(
      fetchUploadBidFilesUrl(queryParams)
    );

    if (!response.success) throw response;

    return response.data?.data;
  };

  /**
   * Api call to download upload bid file template.
   */
  public downloadUploadBidFileTemplate = async (categoryId: string) => {
    const response = await this.get<BlobPart>(
      downloadUploadBidFileTemplateUrl(categoryId),
      {
        responseType: 'blob',
      }
    );

    if (!response.success) throw response;

    return response?.data;
  };

  /**
   * Api call to upload bid file by category.
   */
  public uploadBidFile = async ({ categoryId, file }: IUploadFileOptions) => {
    const response = await this.post<IUploadFileResponse>(
      uploadBidFileUrl(categoryId),
      file,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );

    if (!response.success) throw response;

    return response?.data;
  };

  /**
   * Api call to fetch the uploaded bids by category.
   *
   * @returns IFetchAllUploadedBidsResponse
   */
  public searchAllUploadedBids = async (
    queryParams: IFetchAllUploadedBidsPayload
  ) => {
    const response = await this.get<IFetchAllUploadedBidsResponse>(
      uploadedBidSearchUrl(queryParams)
    );

    if (!response.success) throw response;

    return formatUploadedBids(response.data.data);
  };

  /**
   * Api call to fetch the staged bids report by category.
   *
   * @returns IFetchAllStagedBidsReportResponse
   */
  public searchAllStagedBidsReport = async (
    queryParams: IFetchAllStagedBidsReportPayload
  ) => {
    const response = await this.get<IFetchAllStagedBidsReportResponse>(
      stagedBidsReportSearchUrl(queryParams)
    );

    if (!response.success) throw response;

    return formatStagedBidsReport(response.data.data);
  };

  /**
   * Api call to download the excel bid file template.
   */
  public downloadBidFileTemplate = async (
    queryParams: IFetchDownloadBidsTemplatePayload
  ) => {
    const response = await this.get<BlobPart>(
      downloadBidFileTemplateUrl(queryParams),
      {
        responseType: 'blob',
        timeout: 60 * 1000,
      }
    );

    if (!response.success) throw response;

    return response?.data;
  };

  /**
   * Api call to upload Bid book in excel format.
   */
  public uploadBidBookTemplate = async (
    payload: IUploadBidBookTemplatePayload
  ) => {
    const response = await this.post<IUploadFileResponse>(
      uploadBidTemplateFileUrl(payload.categoryId),
      payload.file,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        timeout: 1 * 60 * 1000, // Force a 1 minute wait on uploads.
      }
    );

    if (!response.success) throw response;

    return response;
  };

  /**
   * API call to get bidding window info
   */
  public fetchBiddingWindowInfo = async () => {
    const response = await this.get<IFetchBiddingWindowInfoResponse>(
      fetchBiddingWindowInfoUrl()
    );

    if (!response.success) throw response;

    return formatBiddingInfoResponse(response.data?.data);
  };

  /**
   * API call to get timed auction time period info
   */
  public fetchTimedAuctionTimePeriod = async () => {
    const response = await this.get<IFetchTimedAuctionTimePeriodResponse>(
      fetchTimedAuctionTimePeriodUrl()
    );

    if (!response.success) throw response;

    return formatTimedAuctionInfoResponse(response.data?.data);
  };

  /**
   * Api call to fetch staged bids data.
   */
  public fetchStagedBids = async (queryParams: IFetchStagedBidsPayload) => {
    const response = await this.get<IFetchStagedBidsResponse>(
      fetchStagedBidsUrl(queryParams)
    );

    if (!response.success) throw response;

    return formatStagedBids(response?.data?.data);
  };

  /**
   * Api call to fetch bid validity config
   */
  public fetchBidValidityConfiguration = async (
    params: IBidValidityConfigurationQueryParams
  ) => {
    const response = await this.get<IBidValidityConfigurationsResponse>(
      fetchBidValidityConfigurationUrl(params)
    );

    if (!response.success) throw response;

    return response.data?.data.configurations;
  };

  /**
   * Api call to fetch bidding windows by bid validity configuration ID
   */
  public fetchBiddingWindowsByValidityConfig = async (
    bidValidityConfigId: string
  ) => {
    const response = await this.get<IBiddingWindowsResponse>(
      fetchBiddingWindowsByValidityConfigUrl(bidValidityConfigId)
    );

    if (!response.success) throw response;

    return response.data?.data.biddingWindows;
  };

  /**
   * Api call to fetch the ranks detailed report admin data.
   * Accepts optional filter for categoryId and brandId.
   */
  public fetchRanksDetailedReportAdmin = async (
    filter?: IRanksDetailedReportAdminFilterQueryParams
  ) => {
    const response = await this.get<IRanksDetailedReportAdminResponseRaw>(
      fetchRanksDetailedReportAdminUrl(filter)
    );

    if (!response.success) throw response;

    return formatBidEfficiencyData(response.data.data);
  };

  /**
   * Api call to fetch the store details by store ID.
   */
  public fetchStoreDetails = async (storeId: string) => {
    const response = await this.get<IStoreDetailsResponseRaw>(
      fetchStoreDetailsUrl(storeId)
    );

    if (!response.success) throw response;

    return response.data.data;
  };

  /**
   * API call to export bids data to Excel.
   */
  public exportBidsExcel = async (queryParams?: IExportBidsQueryOptions) => {
    const { fromDate, toDate, ...bodyParams } = queryParams || {};

    const url = fetchExportBidsExcelUrl(
      fromDate || toDate ? { fromDate, toDate } : undefined
    );

    const hasBodyParams = Object.keys(bodyParams).length > 0;
    const response = await this.post<BlobPart>(
      url,
      hasBodyParams ? bodyParams : undefined,
      {
        responseType: 'blob',
        timeout: 60 * 1000,
      }
    );

    if (!response.success) throw response;

    return response.data;
  };

  /**
   * New API call to download dealer bid template excel file (category + brand scoped)
   */
  public downloadDealerBidTemplate = async (
    brandId: string,
    categoryId: string
  ) => {
    const response = await this.get<BlobPart>(
      downloadDealerBidTemplateUrl(brandId, categoryId),
      {
        responseType: 'blob',
        timeout: 60 * 1000,
      }
    );

    if (!response.success) throw response;

    return response.data;
  };
}

/**
 * This creates a new instance of the class. is th base Axios API client Class
 * wrapper for All User Accounts related requests.
 */
export const BIDS_API = BidsAPIClient.getClientInstance();
